import type * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { SHIRES, type ShireId } from './electorate-shires';

const MY_SHIRE_KEY = 'wb:my-shire';

function getStoredShire(): ShireId | null {
  if (typeof window === 'undefined') return null;
  const v = window.localStorage.getItem(MY_SHIRE_KEY);
  if (!v) return null;
  return SHIRES.some((s) => s.id === v) ? (v as ShireId) : null;
}

function setStoredShire(id: ShireId) {
  window.localStorage.setItem(MY_SHIRE_KEY, id);
  // Same-tab listeners (e.g. Common Tasks tiles on the homepage) — the
  // browser's `storage` event only fires in OTHER tabs, so we dispatch a
  // custom event for in-tab subscribers.
  window.dispatchEvent(new CustomEvent('wb:my-shire-change', { detail: id }));
}


type Intent = 'my-shire' | 'contact';

export function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mySlug, setMySlug] = useState<ShireId | null>(null);
  const [pickerFor, setPickerFor] = useState<Intent | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setMySlug(getStoredShire());
    setHydrated(true);
  }, []);

  // Note: we deliberately do NOT auto-set "My Shire" from the URL. A user
  // browsing other shires (or looking on behalf of a friend) shouldn't have
  // their saved choice silently overwritten. The only way mySlug changes is
  // when the user picks one explicitly via the picker below.

  const goToMyShire = useCallback(() => {
    const slug = mySlug ?? getStoredShire();
    if (!slug) {
      setPickerFor('my-shire');
      return;
    }
    // If you're already on your shire's page, tapping again re-opens the
    // picker — it's the most discoverable place for "change".
    if (location.pathname === `/shire/${slug}`) {
      setPickerFor('my-shire');
      return;
    }
    navigate({ to: '/shire/$name', params: { name: slug } });
  }, [mySlug, navigate, location.pathname]);




  const handlePick = useCallback(
    (id: ShireId) => {
      setStoredShire(id);
      setMySlug(id);
      const intent = pickerFor;
      setPickerFor(null);
      if (intent === 'contact') {
        navigate({ to: '/contact', search: { from: id } });
      } else {
        navigate({ to: '/shire/$name', params: { name: id } });
      }
    },
    [pickerFor, navigate],
  );

  const isMap = location.pathname === '/';
  const isProfile = location.pathname.startsWith('/profile');
  const onMyShire = !!mySlug && location.pathname === `/shire/${mySlug}`;

  const myShireName = hydrated && mySlug
    ? SHIRES.find((s) => s.id === mySlug)?.name ?? null
    : null;

  return (
    <>
      <nav className="mobile-bottom-nav no-print" aria-label="Quick navigation">
        <NavBtn label="Map" icon={MapIcon} active={isMap} onClick={() => navigate({ to: '/' })} />
        <MyShireCell
          subtitle={myShireName}
          active={onMyShire}
          onMain={goToMyShire}
          onChange={() => setPickerFor('my-shire')}
        />
        <NavBtn
          label="Profile"
          icon={UserIcon}
          active={isProfile}
          onClick={() => navigate({ to: '/profile' })}
        />
        <NavLink label="Raise with Bevan" icon={MailIcon} href="https://raiseit.bevaneatts.com.au/welcome" />
      </nav>

      {pickerFor && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Choose your shire"
          className="mobile-shire-picker no-print"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPickerFor(null);
          }}
        >
          <div className="mobile-shire-picker__panel">
            <div className="mobile-shire-picker__head">
              <h2>Which shire are you in?</h2>
              <button
                type="button"
                onClick={() => setPickerFor(null)}
                aria-label="Close"
                className="mobile-shire-picker__close"
              >
                ×
              </button>
            </div>
            <p className="mobile-shire-picker__hint">
              We'll remember this so "My Shire" takes you straight there.
            </p>
            <ul className="mobile-shire-picker__list">
              {SHIRES.map((s) => (
                <li key={s.id}>
                  <button type="button" onClick={() => handlePick(s.id)}>
                    {s.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

function NavBtn({
  label,
  icon: Icon,
  active,
  onClick,
}: {
  label: string;
  icon: (props: { active?: boolean }) => React.ReactElement;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`mobile-bottom-nav__btn${active ? ' is-active' : ''}`}
      aria-current={active ? 'page' : undefined}
    >
      <Icon active={active} />
      <span>{label}</span>
    </button>
  );
}

function NavLink({
  label,
  icon: Icon,
  href,
}: {
  label: string;
  icon: (props: { active?: boolean }) => React.ReactElement;
  href: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="mobile-bottom-nav__btn"
    >
      <Icon />
      <span>{label}</span>
    </a>
  );
}

/**
 * "My Shire" nav cell. Label stays "My Shire" so users always know what they're
 * tapping; once a shire is saved, its name appears as a subtitle below and a
 * small inline "Change" affordance lets the user re-pick without it reading as
 * a system alert.
 */
function MyShireCell({
  subtitle,
  active,
  onMain,
  onChange,
}: {
  subtitle: string | null;
  active?: boolean;
  onMain: () => void;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onMain}
      className={`mobile-bottom-nav__btn${active ? ' is-active' : ''}`}
      aria-current={active ? 'page' : undefined}
      aria-label={subtitle ? `My Shire: ${subtitle}` : 'My Shire'}
      style={{ flex: 1 }}
    >
      <HomeIcon />
      <span>My Shire</span>
      {subtitle ? (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            marginTop: 1,
            fontSize: '0.68rem',
            fontWeight: 600,
            color: 'var(--ink-mid)',
            lineHeight: 1,
            maxWidth: '100%',
          }}
        >
          <span
            style={{
              maxWidth: '8ch',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {subtitle}
          </span>
          <span
            role="button"
            tabIndex={0}
            aria-label="Change my shire"
            onClick={(e) => {
              e.stopPropagation();
              onChange();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onChange();
              }
            }}
            style={{
              color: 'var(--rivergum)',
              textDecoration: 'underline',
              textUnderlineOffset: 2,
              cursor: 'pointer',
              padding: '0 2px',
            }}
          >
            Change
          </span>
        </span>
      ) : null}
    </button>
  );
}


const stroke = { stroke: 'currentColor', strokeWidth: 1.8, fill: 'none', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

function MapIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" style={stroke}>
      <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2Z" />
      <path d="M9 4v14" />
      <path d="M15 6v14" />
    </svg>
  );
}
function HomeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" style={stroke}>
      <path d="M3 11 12 4l9 7" />
      <path d="M5 10v10h14V10" />
    </svg>
  );
}
function UserIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" style={stroke}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c1.5-3.5 4-5 7-5s5.5 1.5 7 5" />
    </svg>
  );
}
function MailIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" style={stroke}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}
